import { Head, Link } from "@inertiajs/react"
import { Download } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import AppLogoIcon from "@/components/app-logo-icon"
import { ScoreRadarChart } from "@/components/charts/ScoreRadarChart"
import {
  getDescriptorLabel,
  getDescriptorColor,
} from "@/components/DescriptorModal"

interface Sample {
  id: number
  name: string
  origin?: string
  land?: string
  producer?: string
  variety?: string
  process?: string
}

interface UserSample {
  id: number
  sample: Sample
  roast_level?: string
  fragrance_score: number
  acidity_score: number
  body_score: number
  flavor_score: number
  aftertaste_score: number
  balance_score: number
  overall_score: number
  uniformity_score: number
  clean_cup_score: number
  sweetness_score: number
  defects_cups: number
  defects_intensity: number
  notes: string
  total_score: number
  aroma_rating?: number
  non_uniform_cups?: number[]
  defective_cups_list?: number[]
  user_name?: string
  aroma_sca_selections?: Record<string, string[]>
}

interface Organization {
  name: string
  logo_url?: string
}

interface Cupping {
  id: number
  title: string
  created_at: string
  cupping_form?: string
  participant_count?: number
  organization?: Organization
}

interface Props {
  cupping: Cupping
  user_sample: UserSample
}

const protocolLabels: Record<string, string> = {
  arabica: "Arabica",
  robusta: "Robusta",
  cup_of_excellence: "Taza de Excelencia",
  cva_affective: "CVA Afectivo",
  cva_descriptive: "CVA Descriptivo",
  cva_combined: "CVA Combinado",
  sca_cva_affective: "SCA CVA Afectivo",
  sca_cva_descriptive: "SCA CVA Descriptivo",
  sca_cva_combined: "SCA CVA Combinado",
}

const isCvaProtocol = (form?: string) => !!form && form.includes("cva")

const getScoreGrade = (score: number): string => {
  if (score >= 90) return "A+"
  if (score >= 85) return "A"
  if (score >= 80) return "B+"
  if (score >= 75) return "B"
  if (score >= 70) return "C"
  return "D"
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return "bg-green-600"
  if (score >= 85) return "bg-green-500"
  if (score >= 80) return "bg-blue-500"
  if (score >= 75) return "bg-primary"
  if (score >= 70) return "bg-yellow-500"
  return "bg-red-500"
}

const getRoastLevelColor = (level: string): string => {
  switch (level) {
    case "light":
      return "#D4A574"
    case "mid-light":
      return "#B8865F"
    case "medium":
      return "#8B5A3C"
    case "mid-dark":
      return "#6B4423"
    case "dark":
      return "#3E2723"
    default:
      return "#8B5A3C"
  }
}

const getRoastLevelLabel = (level: string): string => {
  switch (level) {
    case "light":
      return "Claro"
    case "mid-light":
      return "Medio-Claro"
    case "medium":
      return "Medio"
    case "mid-dark":
      return "Medio-Oscuro"
    case "dark":
      return "Oscuro"
    default:
      return level
  }
}

export default function PublicReport({ cupping, user_sample }: Props) {
  const totalScore = user_sample.total_score || 0
  const grade = getScoreGrade(totalScore)
  const scoreColor = getScoreColor(totalScore)
  const isCva = isCvaProtocol(cupping.cupping_form)

  const handleDownloadPDF = () => {
    import("jspdf").then(({ jsPDF }) => {
      const doc = new jsPDF()
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 20
      const green: [number, number, number] = [19, 160, 107]
      const slate: [number, number, number] = [61, 79, 92]
      const lightGray: [number, number, number] = [245, 245, 245]
      const borderGray: [number, number, number] = [220, 220, 220]
      let y = 0

      doc.setFillColor(...slate)
      doc.rect(0, 0, pageW, 38, "F")

      const drawContent = (logoBase64?: string) => {
        if (logoBase64) {
          try {
            doc.addImage(logoBase64, "PNG", margin, 5, 28, 28)
          } catch (_) {}
        }
        doc.setFont("helvetica", "bold")
        doc.setFontSize(16)
        doc.setTextColor(255, 255, 255)
        doc.text("Catador Pro", margin + 32, 17)
        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(180, 220, 200)
        doc.text("Plataforma de Análisis Sensorial", margin + 32, 24)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(9)
        doc.text("REPORTE DE CATACIÓN", pageW - margin, 22, { align: "right" })

        y = 50
        doc.setFont("helvetica", "bold")
        doc.setFontSize(17)
        doc.setTextColor(...slate)
        doc.text(user_sample.sample.name, margin, y)
        y += 7
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        doc.setTextColor(120, 120, 120)
        doc.text(
          `Sesión TS-${cupping.id}  ·  ${cupping.title}  ·  ${new Date(cupping.created_at).toLocaleDateString("es")}`,
          margin,
          y,
        )
        y += 10
        doc.setDrawColor(...green)
        doc.setLineWidth(1.2)
        doc.line(margin, y, pageW - margin, y)
        y += 10

        doc.setFillColor(...green)
        doc.roundedRect(margin, y, 60, 22, 4, 4, "F")
        doc.setFont("helvetica", "bold")
        doc.setFontSize(20)
        doc.setTextColor(255, 255, 255)
        doc.text(totalScore.toFixed(1), margin + 30, y + 14, {
          align: "center",
        })
        doc.setFont("helvetica", "normal")
        doc.setFontSize(9)
        doc.text("Puntuación Total", margin + 30, y + 20, { align: "center" })
        y += 32

        const scoreRows = isCva
          ? [
              { label: "Fragancia", value: user_sample.fragrance_score },
              { label: "Aroma", value: user_sample.aroma_rating ?? 0 },
              { label: "Sabor", value: user_sample.flavor_score },
              { label: "Retrogusto", value: user_sample.aftertaste_score },
              { label: "Acidez", value: user_sample.acidity_score },
              { label: "Dulzura", value: user_sample.sweetness_score },
              { label: "Cuerpo", value: user_sample.body_score },
              { label: "General", value: user_sample.overall_score },
            ]
          : [
              { label: "Fragancia/Aroma", value: user_sample.fragrance_score },
              { label: "Acidez", value: user_sample.acidity_score },
              { label: "Cuerpo", value: user_sample.body_score },
              { label: "Sabor", value: user_sample.flavor_score },
              { label: "Retrogusto", value: user_sample.aftertaste_score },
              { label: "Balance", value: user_sample.balance_score },
              { label: "General", value: user_sample.overall_score },
            ]

        const rowH = 9
        scoreRows.forEach(({ label, value }, i) => {
          const bg =
            i % 2 === 0
              ? lightGray
              : ([255, 255, 255] as [number, number, number])
          doc.setFillColor(...bg)
          doc.rect(margin, y, pageW - margin * 2, rowH, "F")
          doc.setDrawColor(...borderGray)
          doc.setLineWidth(0.2)
          doc.rect(margin, y, pageW - margin * 2, rowH)
          doc.setFont("helvetica", "normal")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text(label, margin + 4, y + 6.2)
          doc.setFont("helvetica", "bold")
          doc.setTextColor(...green)
          doc.text(String(value), pageW - margin - 4, y + 6.2, {
            align: "right",
          })
          y += rowH
        })

        if (user_sample.notes) {
          y += 10
          doc.setFont("helvetica", "bold")
          doc.setFontSize(10)
          doc.setTextColor(...slate)
          doc.text("Notas del catador", margin, y)
          y += 6
          doc.setFont("helvetica", "normal")
          doc.setFontSize(9)
          doc.setTextColor(80, 80, 80)
          const lines = doc.splitTextToSize(
            user_sample.notes,
            pageW - margin * 2,
          )
          doc.text(lines, margin, y)
        }

        doc.setFillColor(...slate)
        doc.rect(0, pageH - 14, pageW, 14, "F")
        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor(180, 220, 200)
        doc.text("Catador Pro · catas.sabersobrecafe.com", margin, pageH - 5)
        doc.text(
          `Generado el ${new Date().toLocaleDateString("es")}`,
          pageW - margin,
          pageH - 5,
          { align: "right" },
        )

        doc.save(`reporte-${user_sample.sample.name}-TS${cupping.id}.pdf`)
      }

      fetch("/icon.png")
        .then((res) => res.blob())
        .then(
          (blob) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result as string)
              reader.onerror = reject
              reader.readAsDataURL(blob)
            }),
        )
        .then((base64) => drawContent(base64))
        .catch(() => drawContent())
    })
  }

  return (
    <>
      <Head title={`Reporte · ${user_sample.sample.name}`} />

      <div className="bg-background min-h-screen">
        {/* Public header */}
        <header className="bg-background/95 border-b backdrop-blur">
          <div className="container mx-auto flex h-14 items-center justify-between px-4">
            <Link href="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md">
                <AppLogoIcon className="h-8 w-8" />
              </div>
              <span className="font-semibold">Catador Pro</span>
            </Link>
            <Button
              className="bg-primary hover:bg-primary/90 gap-2"
              onClick={handleDownloadPDF}
            >
              <Download className="h-4 w-4" />
              Descargar PDF
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="container mx-auto max-w-5xl px-4 py-10">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content Area */}
            <div className="space-y-6 lg:col-span-2">
              {/* Sample Info Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="text-xl font-bold">
                        {user_sample.sample.name} (TS-{cupping.id})
                      </h1>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">
                          Protocolo:
                        </span>
                        <span className="text-primary text-sm font-semibold">
                          {protocolLabels[cupping.cupping_form || ""] ||
                            cupping.cupping_form ||
                            "N/A"}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">
                        {new Date(cupping.created_at).toLocaleDateString("es", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-muted-foreground text-sm">
                        Puntuacion
                      </div>
                      <div className="text-primary text-2xl font-bold">
                        {totalScore.toFixed(1)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sample Details Card */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 text-lg font-semibold">
                    Detalles de la Muestra
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      {user_sample.sample.origin && (
                        <div className="flex justify-between border-b pb-2">
                          <span className="text-muted-foreground">Pais</span>
                          <span className="font-medium">
                            {user_sample.sample.origin}
                          </span>
                        </div>
                      )}
                      {user_sample.sample.land && (
                        <div className="flex justify-between border-b pb-2">
                          <span className="text-muted-foreground">
                            Ubicacion
                          </span>
                          <span className="font-medium">
                            {user_sample.sample.land}
                          </span>
                        </div>
                      )}
                      {user_sample.sample.producer && (
                        <div className="flex justify-between border-b pb-2">
                          <span className="text-muted-foreground">
                            Productor
                          </span>
                          <span className="font-medium">
                            {user_sample.sample.producer}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {user_sample.sample.variety && (
                        <div className="flex justify-between border-b pb-2">
                          <span className="text-muted-foreground">
                            Varietal
                          </span>
                          <span className="font-medium">
                            {user_sample.sample.variety}
                          </span>
                        </div>
                      )}
                      {user_sample.sample.process && (
                        <div className="flex justify-between border-b pb-2">
                          <span className="text-muted-foreground">Proceso</span>
                          <span className="font-medium capitalize">
                            {user_sample.sample.process}
                          </span>
                        </div>
                      )}
                      {user_sample.roast_level && (
                        <div className="flex items-center justify-between border-b pb-2">
                          <span className="text-muted-foreground">
                            Nivel de tueste
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {getRoastLevelLabel(user_sample.roast_level)}
                            </span>
                            <div
                              className="h-5 w-5 rounded-full border"
                              style={{
                                backgroundColor: getRoastLevelColor(
                                  user_sample.roast_level,
                                ),
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Score Breakdown */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 text-lg font-bold">Puntuaciones</h3>
                  <div className="space-y-4">
                    {isCva ? (
                      <>
                        {[
                          {
                            label: "Fragancia",
                            value: user_sample.fragrance_score,
                          },
                          { label: "Aroma", value: user_sample.aroma_rating },
                          { label: "Sabor", value: user_sample.flavor_score },
                          {
                            label: "Retrogusto",
                            value: user_sample.aftertaste_score,
                          },
                          { label: "Acidez", value: user_sample.acidity_score },
                          {
                            label: "Dulzura",
                            value: user_sample.sweetness_score,
                          },
                          {
                            label: "Cuerpo / Sensacion en boca",
                            value: user_sample.body_score,
                          },
                          {
                            label: "General",
                            value: user_sample.overall_score,
                          },
                        ].map(({ label, value }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between border-b pb-3"
                          >
                            <span className="text-muted-foreground">
                              {label}
                            </span>
                            <span className="text-primary font-medium">
                              {value}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between border-b pb-3">
                          <span className="text-muted-foreground">
                            Tazas no uniformes
                          </span>
                          <span className="font-medium">
                            {user_sample.non_uniform_cups?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between border-b pb-3">
                          <span className="text-muted-foreground">
                            Tazas defectuosas
                          </span>
                          <span className="font-medium">
                            {user_sample.defective_cups_list?.length || 0}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {[
                          {
                            label: "Fragancia / Aroma",
                            value: user_sample.fragrance_score,
                          },
                          { label: "Acidez", value: user_sample.acidity_score },
                          { label: "Cuerpo", value: user_sample.body_score },
                          { label: "Sabor", value: user_sample.flavor_score },
                          {
                            label: "Retrogusto",
                            value: user_sample.aftertaste_score,
                          },
                          {
                            label: "Balance",
                            value: user_sample.balance_score,
                          },
                          {
                            label: "General",
                            value: user_sample.overall_score,
                          },
                          {
                            label: "Uniformidad",
                            value: user_sample.uniformity_score,
                          },
                          {
                            label: "Taza limpia",
                            value: user_sample.clean_cup_score,
                          },
                          {
                            label: "Dulzura",
                            value: user_sample.sweetness_score,
                          },
                        ].map(({ label, value }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between border-b pb-3"
                          >
                            <span className="text-muted-foreground">
                              {label}
                            </span>
                            <span className="text-primary font-medium">
                              {value}
                            </span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between border-b pb-3">
                          <span className="text-muted-foreground">
                            Defectos
                          </span>
                          <span className="font-medium">
                            {user_sample.defects_cups || 0}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Notes */}
                    {user_sample.notes && (
                      <div className="pt-2">
                        <div className="text-muted-foreground mb-2 text-sm font-medium">
                          Notas del catador
                        </div>
                        <p className="text-sm">{user_sample.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6 lg:col-span-1">
              {/* Score Badge Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="mb-4 text-center">
                    <div className="text-muted-foreground text-sm">
                      Puntuacion
                    </div>
                    <div className="text-4xl font-bold">
                      {totalScore.toFixed(1)}
                    </div>
                  </div>

                  <div className="mb-4 flex items-center justify-center">
                    <div className="bg-primary flex items-center gap-2 rounded-full px-4 py-2">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full ${scoreColor} font-bold text-white`}
                      >
                        {grade}
                      </div>
                      <span className="font-medium text-white">
                        {user_sample.user_name || "Catador"}
                      </span>
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                        <span className="text-primary text-lg font-bold">
                          {totalScore.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {cupping.participant_count !== undefined && (
                    <div className="text-muted-foreground text-center text-sm">
                      {cupping.participant_count}{" "}
                      {cupping.participant_count === 1
                        ? "participante"
                        : "participantes"}
                    </div>
                  )}

                  {cupping.organization && (
                    <div className="mt-4 border-t pt-4 text-center">
                      <p className="text-muted-foreground text-xs">
                        Evaluado por
                      </p>
                      <p className="text-primary text-sm font-medium">
                        {cupping.organization.name}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Radar Chart */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="mb-4 text-center text-sm font-semibold">
                    Perfil Sensorial
                  </h3>
                  <div className="h-[280px] w-full">
                    <ScoreRadarChart
                      protocol={isCva ? "cva" : "sca"}
                      scores={user_sample}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Descriptors */}
              {user_sample.aroma_sca_selections &&
                typeof user_sample.aroma_sca_selections === "object" &&
                Object.keys(user_sample.aroma_sca_selections).length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="mb-4 text-sm font-semibold">
                        Descriptores
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(user_sample.aroma_sca_selections).map(
                          ([category, descriptors]) => {
                            if (!descriptors || descriptors.length === 0)
                              return null
                            return (
                              <div key={category}>
                                <p className="text-muted-foreground mb-1 text-xs font-medium capitalize">
                                  {category === "fragrance"
                                    ? "Fragancia / Aroma"
                                    : category === "acidity"
                                      ? "Acidez"
                                      : category === "body"
                                        ? "Cuerpo"
                                        : category === "flavor"
                                          ? "Sabor"
                                          : category === "aftertaste"
                                            ? "Retrogusto"
                                            : category === "balance"
                                              ? "Balance"
                                              : category}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {descriptors.map((d) => (
                                    <span
                                      key={d}
                                      className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                                      style={{
                                        backgroundColor:
                                          getDescriptorColor(d) ||
                                          "hsl(var(--primary))",
                                      }}
                                    >
                                      {getDescriptorLabel(d)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )
                          },
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 space-y-4 text-center">
            <p className="text-muted-foreground text-xs">
              Generado con{" "}
              <span className="text-primary font-medium">Catador Pro</span> ·
              catas.sabersobrecafe.com
            </p>
            <Link
              href="/"
              className="text-primary hover:text-primary/80 text-sm font-medium"
            >
              Crea tu propia catacion gratis
            </Link>
          </div>
        </main>
      </div>
    </>
  )
}
